import type { ColumnFiltersState } from '@tanstack/react-table'
import { z } from 'zod'

/**
 * Column filters, as the DataTable's header funnels set them.
 *
 * One value shape per column kind. The table stores them in TanStack's
 * `ColumnFiltersState` (`{ id, value }[]`); a page that filters in the
 * browser applies them with `applyColumnFilters`, and one that filters on
 * the server sends `toFilterParams` — the backend's `column:op:value` form.
 */

export const NUMBER_OPS = ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'] as const
export type NumberOp = (typeof NUMBER_OPS)[number]

export type NumberFilter =
  { op: NumberOp; value: number } | { op: 'between'; min: number; max: number }
export type TextFilter = { op: 'contains'; value: string }
export type SelectFilter = { op: 'in'; values: string[] }
/**
 * "Select all matching" in a value list: every value — or every value
 * containing `contains` — but the ones unticked after.
 */
export type ExceptFilter = { op: 'notIn'; values: string[]; contains?: string }
export type ColumnFilterValue = NumberFilter | TextFilter | SelectFilter | ExceptFilter

/** How the API names a missing or empty value in a value filter. */
export const BLANK_VALUE = '__blank__'

/** A column filter as a URL carries it: one funnel's value, checked like any other input. */
export const columnFilterValueSchema = z.union([
  z.object({ op: z.enum(NUMBER_OPS), value: z.number() }),
  z.object({ op: z.literal('between'), min: z.number(), max: z.number() }),
  z.object({ op: z.literal('contains'), value: z.string().max(200) }),
  z.object({ op: z.literal('in'), values: z.array(z.string().max(200)).max(500) }),
  z.object({
    op: z.literal('notIn'),
    values: z.array(z.string().max(200)).max(500),
    contains: z.string().max(200).optional(),
  }),
])

/** Every column's filter, for a page that keeps its grid's funnels in the URL. */
export const columnFiltersSchema = z
  .array(z.object({ id: z.string().max(64), value: columnFilterValueSchema }))
  .max(50)

export function isColumnFilterValue(value: unknown): value is ColumnFilterValue {
  return typeof value === 'object' && value !== null && 'op' in value
}

/** A cell as text; an object or a null is nothing to match on. */
function text(cell: unknown): string {
  return typeof cell === 'string'
    ? cell
    : typeof cell === 'number' || typeof cell === 'boolean'
      ? String(cell)
      : ''
}

/** Does one cell's raw value pass a filter? Null never passes a number filter. */
export function matchesFilter(cell: unknown, filter: ColumnFilterValue): boolean {
  switch (filter.op) {
    case 'contains': {
      const needle = filter.value.trim().toLocaleLowerCase()
      if (!needle) return true
      return text(cell).toLocaleLowerCase().includes(needle)
    }
    case 'in':
      return filter.values.length === 0 || filter.values.includes(text(cell))
    case 'notIn': {
      const value = text(cell)
      const needle = filter.contains?.trim().toLocaleLowerCase()
      if (needle && !value.toLocaleLowerCase().includes(needle)) return false
      return !filter.values.includes(value)
    }
    default: {
      if (cell === null || cell === undefined || cell === '') return false
      const number = typeof cell === 'number' ? cell : Number(cell)
      if (Number.isNaN(number)) return false
      switch (filter.op) {
        case 'eq':
          return number === filter.value
        case 'ne':
          return number !== filter.value
        case 'gt':
          return number > filter.value
        case 'gte':
          return number >= filter.value
        case 'lt':
          return number < filter.value
        case 'lte':
          return number <= filter.value
        case 'between':
          return number >= filter.min && number <= filter.max
      }
    }
  }
}

/**
 * Client mode: the rows that pass every filter. `accessors` maps a column id
 * to how its raw value is read from a row.
 */
export function applyColumnFilters<T>(
  rows: readonly T[],
  filters: ColumnFiltersState,
  accessors: Record<string, (row: T) => unknown>,
): T[] {
  const active = filters.filter(
    (filter): filter is { id: string; value: ColumnFilterValue } =>
      isColumnFilterValue(filter.value) && filter.id in accessors,
  )
  if (active.length === 0) return [...rows]
  return rows.filter((row) =>
    active.every((filter) => matchesFilter(accessors[filter.id]!(row), filter.value)),
  )
}

const FILTER_RE = /^([a-z0-9_]+):([a-z]+):(-?\d+(?:\.\d+)?)$/
/** A text column's value list: one value per entry, `__blank__` for none. */
const VALUE_RE = /^([a-z0-9_]+):(in|nin):(.+)$/
/** A text column's `contains`: any value, the backend matches it case-insensitively. */
const CONTAINS_RE = /^([a-z0-9_]+):contains:(.+)$/

function isNumberOp(op: string): op is NumberOp {
  return (NUMBER_OPS as readonly string[]).includes(op)
}

/**
 * The inverse of `toFilterParams`: `column:op:value` entries as column filters — a
 * number comparison, or a text column's `contains`. A
 * `gte` and an `lte` on the same column fold back into the one `between`
 * the funnel wrote them from; anything the pattern rejects is dropped
 * rather than sent on to the API as a 422 the page cannot explain.
 */
export function fromFilterParams(filters: readonly string[]): ColumnFiltersState {
  const byColumn = new Map<string, NumberFilter[]>()
  const contains: ColumnFiltersState = []
  const listed = new Map<string, { in: string[]; nin: string[] }>()
  const matching = new Map<string, string>()
  for (const entry of filters) {
    const values = VALUE_RE.exec(entry)
    if (values) {
      const [, id = '', op = '', raw = ''] = values
      const list = listed.get(id) ?? { in: [], nin: [] }
      list[op as 'in' | 'nin'].push(raw === BLANK_VALUE ? '' : raw)
      listed.set(id, list)
      continue
    }
    const text = CONTAINS_RE.exec(entry)
    if (text) {
      const [, id = '', value = ''] = text
      matching.set(id, value)
      continue
    }
    const match = FILTER_RE.exec(entry)
    if (!match) continue
    const [, id = '', op = '', raw = ''] = match
    if (!isNumberOp(op)) continue
    const list = byColumn.get(id) ?? []
    list.push({ op, value: Number(raw) })
    byColumn.set(id, list)
  }
  for (const [id, value] of matching) {
    // A substring with exclusions is one "select all matching, but these".
    if (!listed.get(id)?.nin.length) contains.push({ id, value: { op: 'contains', value } })
  }
  for (const [id, list] of listed) {
    if (list.nin.length) {
      contains.push({ id, value: { op: 'notIn', values: list.nin, contains: matching.get(id) } })
    } else if (list.in.length) {
      contains.push({ id, value: { op: 'in', values: list.in } })
    }
  }
  const state: ColumnFiltersState = [...contains]
  for (const [id, list] of byColumn) {
    const [first, second] = list
    if (
      list.length === 2 &&
      first?.op === 'gte' &&
      second?.op === 'lte' &&
      'value' in first &&
      'value' in second
    ) {
      state.push({ id, value: { op: 'between', min: first.value, max: second.value } })
    } else {
      for (const filter of list) state.push({ id, value: filter })
    }
  }
  return state
}

/** Server mode: `["spend:gte:100", "sku:contains:ABC"]`. A between is two entries. */
export function toFilterParams(filters: ColumnFiltersState): string[] {
  return filters.flatMap(({ id, value }) => {
    if (!isColumnFilterValue(value)) return []
    switch (value.op) {
      case 'between':
        return [`${id}:gte:${value.min}`, `${id}:lte:${value.max}`]
      case 'contains':
        return value.value.trim() ? [`${id}:contains:${value.value.trim()}`] : []
      case 'in':
        return value.values.map((item) => `${id}:in:${item || BLANK_VALUE}`)
      case 'notIn':
        return [
          ...(value.contains?.trim() ? [`${id}:contains:${value.contains.trim()}`] : []),
          ...value.values.map((item) => `${id}:nin:${item || BLANK_VALUE}`),
        ]
      default:
        return [`${id}:${value.op}:${value.value}`]
    }
  })
}

/** How many filters are set on a column — 1, or 0. Shown on the funnel. */
export function columnFilterCount(filters: ColumnFiltersState, id: string): number {
  return filters.filter((filter) => filter.id === id && isColumnFilterValue(filter.value)).length
}
